<template>
  <div class="training-student-assign-group"
       v-loading="loading"
       element-loading-text="数据加载中，请稍等...">
    <div v-if="studentData" class="form-container">
      <el-form ref="form"
               :model="formData"
               :rules="rules"
               label-width="120px"
               size="mini">
        <!-- 学生基本信息（只读） -->
        <el-form-item label="教学点">
          <span class="readonly-text">{{ studentData.siteName }}</span>
        </el-form-item>
        <el-form-item label="学生">
          <span class="readonly-text">{{ studentData.studentName }}</span>
        </el-form-item>
        <el-form-item label="学号">
          <span class="readonly-text">{{ studentData.regNo }}</span>
        </el-form-item>
        <el-form-item label="年级">
          <span class="readonly-text">{{ studentData.gradeName }}</span>
        </el-form-item>
        <el-form-item label="班级">
          <span class="readonly-text">{{ studentData.className }}</span>
        </el-form-item>
        <el-form-item label="学生状态">
          <span class="readonly-text">{{ studentData.studentStatus }}</span>
        </el-form-item>

        <!-- 可编辑字段 - 三级级联 -->
        <el-form-item label="小组教学点" prop="siteId">
          <el-select v-model="formData.siteId"
                     placeholder="请选择教学点"
                     filterable
                     size="mini"
                     @change="handleSiteChange"
                     style="width: 100%">
            <el-option v-for="site in siteList"
                       :key="site.id"
                       :label="site.name"
                       :value="site.id">
            </el-option>
            <div v-if="siteList.length === 0" slot="empty" style="padding: 10px 0; text-align: center; color: #999;">
              暂无可用教学点
            </div>
          </el-select>
        </el-form-item>

        <el-form-item label="小组班级" prop="classId">
          <el-select v-model="formData.classId"
                     placeholder="请先选择教学点"
                     filterable
                     size="mini"
                     @change="handleClassChange"
                     :disabled="!formData.siteId"
                     v-loading="classLoading"
                     style="width: 100%">
            <el-option v-for="cls in classList"
                       :key="cls.id"
                       :label="cls.name"
                       :value="cls.id">
            </el-option>
            <div v-if="classList.length === 0 && formData.siteId" slot="empty" style="padding: 10px 0; text-align: center; color: #999;">
              该教学点暂无可用班级
            </div>
          </el-select>
        </el-form-item>

        <el-form-item label="小组" prop="groupId">
          <el-select v-model="formData.groupId"
                     placeholder="请先选择班级"
                     filterable
                     size="mini"
                     :disabled="!formData.classId"
                     v-loading="groupLoading"
                     style="width: 100%">
            <el-option v-for="group in groupList"
                       :key="group.id"
                       :label="group.displayName"
                       :value="group.id">
            </el-option>
            <div v-if="groupList.length === 0 && formData.classId" slot="empty" style="padding: 10px 0; text-align: center; color: #999;">
              该班级暂无可用分组
            </div>
          </el-select>
        </el-form-item>

        <el-form-item label="是否组长" prop="isLeader">
          <el-radio-group v-model="formData.isLeader" size="mini">
            <el-radio label="1">是</el-radio>
            <el-radio label="0">否</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <!-- 操作按钮 -->
      <div class="footer-btns">
        <el-button size="mini" @click="handleCancel">取消</el-button>
        <el-button size="mini" type="primary" @click="handleSubmit" :loading="submitting">提交</el-button>
      </div>
    </div>
    <Hint v-else-if="!loading" type="error">
      <div>学生数据加载失败</div>
      <el-button size="mini" type="text" @click="loadData">重新加载</el-button>
    </Hint>
  </div>
</template>

<script>
import Hint from '../../../../../../elements/Hint'
export default {
  name: 'TrainingStudentAssignGroup',
  components: {
    Hint
  },
  props: {
    extraParams: Object,
    visible: Boolean
  },
  data () {
    return {
      loading: false,
      classLoading: false,
      groupLoading: false,
      submitting: false,
      studentId: '',
      studentData: null,
      siteList: [],
      classList: [],
      groupList: [],
      formData: {
        siteId: '',
        classId: '',
        groupId: '',
        isLeader: '0'
      },
      // 从后端查询到的原始分组数据
      originalData: {
        groupId: '',
        isLeader: '0'
      },
      rules: {
        siteId: [
          {required: true, message: '请选择教学点', trigger: 'change'}
        ],
        classId: [
          {required: true, message: '请选择班级', trigger: 'change'}
        ],
        groupId: [
          {required: true, message: '请选择小组', trigger: 'change'}
        ],
        isLeader: [
          {required: true, message: '请选择是否组长', trigger: 'change'}
        ]
      }
    }
  },
  methods: {
    async loadData () {
      this.loading = true
      try {
        // 1. 加载教学点列表
        await this.loadSiteList()
        // 2. 加载学生信息和当前分组信息
        await this.loadStudentInfo()
        // 3. 如果学生有分组，回显级联数据
        if (this.studentData.currentGroupSiteId) {
          await this.loadClassList(this.studentData.currentGroupSiteId)
          if (this.studentData.currentGroupClassId) {
            await this.loadGroupList(this.studentData.currentGroupClassId)
          }
        }
      } catch (error) {
        console.error('加载数据失败:', error)
      } finally {
        this.loading = false
      }
    },
    loadSiteList () {
      return new Promise((resolve, reject) => {
        this.$ajax({
          url: '/workspace/manager/training/student/getSites',
          method: 'post',
          data: this.$qs.stringify({
            batchId: this.extraParams.batchId
          })
        }).then(response => {
          if (response.data && response.data.responseCode === 'SUCCESS') {
            this.siteList = response.data.data || []
            resolve()
          } else {
            this.$message.warning(response.data.message || '获取教学点列表失败')
            reject(new Error('获取教学点列表失败'))
          }
        }, error => {
          this.$message.error('请求发送失败，请稍后重试')
          reject(error)
        })
      })
    },
    loadStudentInfo () {
      return new Promise((resolve, reject) => {
        this.$ajax({
          url: '/workspace/manager/training/student/getStudentInfo',
          method: 'post',
          data: this.$qs.stringify({
            batchId: this.extraParams.batchId,
            studentId: this.studentId
          })
        }).then(response => {
          if (response.data && response.data.responseCode === 'SUCCESS') {
            const data = response.data.data
            this.studentData = data
            // 回显数据
            this.formData.siteId = data.currentGroupSiteId || ''
            this.formData.classId = data.currentGroupClassId || ''
            this.formData.groupId = data.currentGroupId || ''
            this.formData.isLeader = data.isLeader || '0'
            // 保存原始数据
            this.originalData.groupId = data.currentGroupId || ''
            this.originalData.isLeader = data.isLeader || '0'
            resolve()
          } else {
            this.$message.warning(response.data.message || '获取学生信息失败')
            reject(new Error('获取学生信息失败'))
          }
        }, error => {
          this.$message.error('请求发送失败，请稍后重试')
          reject(error)
        })
      })
    },
    loadClassList (siteId) {
      return new Promise((resolve, reject) => {
        this.classLoading = true
        this.$ajax({
          url: '/workspace/manager/training/student/getClasses',
          method: 'post',
          data: this.$qs.stringify({
            batchId: this.extraParams.batchId,
            siteId: siteId
          })
        }).then(response => {
          if (response.data && response.data.responseCode === 'SUCCESS') {
            this.classList = response.data.data || []
            resolve()
          } else {
            this.$message.warning(response.data.message || '获取班级列表失败')
            reject(new Error('获取班级列表失败'))
          }
        }, error => {
          this.$message.error('请求发送失败，请稍后重试')
          reject(error)
        }).finally(() => {
          this.classLoading = false
        })
      })
    },
    loadGroupList (classId) {
      return new Promise((resolve, reject) => {
        this.groupLoading = true
        this.$ajax({
          url: '/workspace/manager/training/student/getGroups',
          method: 'post',
          data: this.$qs.stringify({
            batchId: this.extraParams.batchId,
            classId: classId
          })
        }).then(response => {
          if (response.data && response.data.responseCode === 'SUCCESS') {
            this.groupList = response.data.data || []
            resolve()
          } else {
            this.$message.warning(response.data.message || '获取分组列表失败')
            reject(new Error('获取分组列表失败'))
          }
        }, error => {
          this.$message.error('请求发送失败，请稍后重试')
          reject(error)
        }).finally(() => {
          this.groupLoading = false
        })
      })
    },
    handleSiteChange (siteId) {
      // 教学点变化时，清空班级和小组
      this.formData.classId = ''
      this.formData.groupId = ''
      this.classList = []
      this.groupList = []
      if (siteId) {
        this.loadClassList(siteId)
      }
    },
    handleClassChange (classId) {
      // 班级变化时，清空小组
      this.formData.groupId = ''
      this.groupList = []
      if (classId) {
        this.loadGroupList(classId)
      }
    },
    handleSubmit () {
      this.$refs.form.validate((valid) => {
        if (!valid) {
          return false
        }

        // 检查数据是否有变化
        let hasGroupChange = this.formData.groupId !== this.originalData.groupId
        let hasLeaderChange = this.formData.isLeader !== this.originalData.isLeader

        if (!hasGroupChange && !hasLeaderChange) {
          this.$message.warning('数据未发生变化，无需提交')
          return false
        }

        // 检查是否是组长迁移的情况（使用从后端查询到的原始数据）
        let wasLeader = this.originalData.isLeader === '1'
        let isMovingToNewGroup = this.originalData.groupId && this.formData.groupId !== this.originalData.groupId
        let willBeLeader = this.formData.isLeader === '1'

        if (wasLeader && isMovingToNewGroup && willBeLeader) {
          // 组长迁移到新分组，需要确认
          this.$confirm('该学生是原分组的组长，是否将其设为新分组的组长？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            this.submitForm()
          }).catch(() => {
            // 用户取消，不做任何操作
          })
          return
        }
        this.submitForm()
      })
    },
    submitForm () {
      this.submitting = true
      this.$ajax({
        url: '/workspace/manager/training/student/assignGroup',
        method: 'post',
        data: this.$qs.stringify({
          studentId: this.studentId,
          batchId: this.extraParams.batchId,
          groupId: this.formData.groupId,
          isLeaderCode: this.formData.isLeader
        })
      }).then(response => {
        if (response.data) {
          if (response.data.responseCode === 'SUCCESS') {
            this.$message.success(response.data.data || '修改成功')
            this.$emit('hidePopoverAndRefresh')
          } else {
            this.$message.warning(response.data.message)
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.submitting = false
      })
    },
    handleCancel () {
      this.$emit('hidePopover')
    }
  },
  mounted () {
    this.studentId = this.$attrs['extra-id']
    this.loadData()
  },
  watch: {
    visible: function (val) {
      if (val) {
        this.loadData()
      }
    }
  }
}
</script>

<style lang="scss">
.training-student-assign-group {
  width: 100%;
  height: 100%;
  padding: 20px;
  padding-right: 50px;
  .form-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    .el-form {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      .readonly-text {
        color: #606266;
        line-height: 28px;
      }
    }
    .footer-btns {
      padding-top: 20px;
      border-top: 1px solid #eee;
      text-align: right;
    }
  }
}
</style>
